import React, { useState } from "react";
import { Button } from "components/elements";
import { FindNewFriendModal } from "components/elements/FindNewFriendModal";
import { useCurrentUser } from "hooks/useCurrentUser";
import { trpc } from "lib/trpc";
import { FriendRequestStatus } from "@compass/shared/types";
import { FriendRequests } from "./FriendRequests";
import { MyFriends } from "./MyFriends";

const FriendsTable = () => {
    const queryClient = trpc.useContext();
    const currentUser = useCurrentUser();
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [activeTab, setActiveTab] = useState<"myFriends" | "friendRequests">("myFriends");
    const friendRequestResponseMutation = trpc.users.friendRequestResponse.useMutation();

    const friendRequestMutation = trpc.users.deleteFriendOrRequest.useMutation();

    const handleRemoveFriend = ({ id }: { id: number }) => {
        try {
            friendRequestMutation.mutate(
                { id },
                {
                    onSuccess: () => {
                        queryClient.users.me.invalidate();
                        queryClient.users.getAllUser.invalidate();
                    },
                },
            );
        } catch (err) {
            console.error(err);
        }
    };

    const handleFriendRequestResponse = ({
        id,
        status,
    }: {
        id: number;
        status: FriendRequestStatus;
    }) => {
        try {
            friendRequestResponseMutation.mutate(
                { id: id, status: status },
                {
                    onSuccess: () => {
                        queryClient.users.me.invalidate();
                        queryClient.users.getAllUser.invalidate();
                    },
                },
            );
        } catch (err) {
            console.error(err);
        }
    };

    return (
        <div className="mx-auto mt-6 w-full">
            {/* Top Bar */}
            <div className="mb-4 flex flex-row items-center justify-between">
                <div className="font-sans text-[28px] font-semibold text-[#424242]">Friends</div>
                <div className="flex gap-4">
                    <Button variant="primary" onClick={() => setIsModalOpen(true)}>
                        Find New Friend
                    </Button>
                </div>
            </div>

            <div className="mb-6 rounded-2xl border border-[#c8c8c8] bg-white py-4 px-4">
                {/* Mini Menu (Tabs) */}
                <div className="mb-6 flex items-start font-semibold text-[#424242]">
                    <div
                        className={`mr-6 cursor-pointer py-2 ${activeTab === "myFriends" ? "border-b-2 border-[#08abf2]" : ""}`}
                        onClick={() => setActiveTab("myFriends")}
                    >
                        My friends
                    </div>
                    <div
                        className={`cursor-pointer py-2 ${activeTab === "friendRequests" ? "border-b-2 border-[#08abf2]" : ""}`}
                        onClick={() => setActiveTab("friendRequests")}
                    >
                        <div className="flex flex-row items-center gap-2">
                            <div>Friend requests</div>
                        </div>
                    </div>
                    <div className="ml-1 self-center">
                        {!!currentUser?.receivedFriendRequests?.length && (
                            <span className="flex h-4 w-4 items-center justify-center rounded-full bg-[#08abf2] text-sm text-white">
                                {currentUser?.receivedFriendRequests.length}
                            </span>
                        )}
                    </div>
                </div>

                {/* Centered Main Content */}

                <div className="h-screen">
                    {activeTab === "myFriends" ? (
                        <MyFriends
                            friends={currentUser?.friends}
                            onRemoveFriend={handleRemoveFriend}
                            setModalOpen={setIsModalOpen}
                        />
                    ) : (
                        <FriendRequests
                            requests={currentUser?.receivedFriendRequests}
                            onFriendRequestResponse={handleFriendRequestResponse}
                            setModalOpen={setIsModalOpen}
                        />
                    )}
                </div>
            </div>

            {/* Find New Friend Modal */}
            <FindNewFriendModal isOpen={isModalOpen} setIsOpen={() => setIsModalOpen(false)} />
        </div>
    );
};

export default FriendsTable;
